'use client';

import CourseCard from '@/modules/khoa-hoc/components/CourseCard';
import { useCourses } from '@/hooks/useCourses';
import { useCourseCategories } from '@/hooks/useCourseCategories';
import { Course, CourseCategory } from '@/types/data';
import { useEffect, useState, Suspense, useCallback } from 'react';
import Pagination from '@/components/ui/Pagination';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { Search } from 'lucide-react';
import Link from 'next/link';
import HeroSlider from '@/modules/homepage/components/HeroSlider';
import { useDiscount } from '@/hooks/useDiscount';

interface KhoaHocPageProps {}

const KhoaHocPageContent: React.FC<KhoaHocPageProps> = ({}) => {
  const NUMBER_COURSES_PER_PAGE = 16;
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const { loading, error, searchCourses } = useCourses();
  const { getCourseCategories } = useCourseCategories();
  const { discountPercent } = useDiscount();
  const [courses, setCourses] = useState<Course[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCourses, setTotalCourses] = useState(0);
  const [keySearch, setKeySearch] = useState<string | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState<string>('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const fetchCourses = async (page: number, category: string | null, keySearch?: string) => {
    const { courses, total } = await searchCourses(
      page,
      NUMBER_COURSES_PER_PAGE,
      category,
      keySearch
    );
    setCourses(courses);
    setTotalCourses(total);
  };

  useEffect(() => {
    const pageParam = searchParams.get('page');
    const categoryParam = searchParams.get('category');
    const keySearchParam = searchParams.get('key') || '';

    // Set initial states
    const page = pageParam ? parseInt(pageParam) : 1;
    const category = categoryParam;

    setCurrentPage(page);
    setSelectedCategory(category);
    setKeySearch(keySearchParam);

    // If page param is missing, update URL to include page=1
    if (!pageParam) {
      updateUrlQuery(1, category, keySearchParam);
    }

    // Always fetch courses with current params
    fetchCourses(page, category, keySearchParam);
  }, [searchParams]);

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newPage = 1; // Reset to first page when searching
    setCurrentPage(newPage);
    updateUrlQuery(newPage, selectedCategory, keySearch);
    await fetchCourses(newPage, selectedCategory, keySearch);
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchTerm: string) => {
      // Clear existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Set new timeout
      const timeout = setTimeout(async () => {
        const newPage = 1;
        setCurrentPage(newPage);
        updateUrlQuery(newPage, selectedCategory, searchTerm);
        await fetchCourses(newPage, selectedCategory, searchTerm);
      }, 300); // 300ms delay

      setSearchTimeout(timeout);
    },
    [searchTimeout, selectedCategory, searchCourses]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newKeySearch = e.target.value;
    setKeySearch(newKeySearch);

    // Use debounced search for real-time filtering
    debouncedSearch(newKeySearch);
  };

  const handlerPageChange = async (page: number) => {
    setCurrentPage(page);
    updateUrlQuery(page, selectedCategory, keySearch);
    await fetchCourses(page, selectedCategory, keySearch);
  };

  const updateCategoryName = async (categorySlug: string | null) => {
    if (!categorySlug) {
      setCategoryName('');
      return;
    }
    const categories = await getCourseCategories();
    const findCategoryRecursive = (cats: CourseCategory[]): CourseCategory | undefined => {
      for (const c of cats) {
        if (c.slug === categorySlug) return c;
        if (c.child_categories && c.child_categories.length > 0) {
          const found = findCategoryRecursive(c.child_categories);
          if (found) return found;
        }
      }
      return undefined;
    };

    const category = findCategoryRecursive(categories);
    setCategoryName(category?.name || '');
  };

  // Update category name when selectedCategory changes
  useEffect(() => {
    updateCategoryName(selectedCategory);
  }, [selectedCategory]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const updateUrlQuery = (page: number, category: string | null, keySearch?: string) => {
    const params = new URLSearchParams();

    // Always set page
    params.set('page', page.toString());

    // Set category if exists
    if (category) {
      params.set('category', category);
    }

    // Set search key if exists and not empty
    if (keySearch && keySearch.trim()) {
      params.set('key', keySearch.trim());
    }

    // Update URL
    const newUrl = `${pathname}?${params.toString()}`;
    router.push(newUrl, { scroll: false });
  };

  return (
    <>
      <HeroSlider data-component-id="src/app/khoa-hoc/page.tsx:169:6" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="169" data-component-end-line="169" data-component-file="page.tsx" data-component-name="HeroSlider" data-component-content="%7B%22elementName%22%3A%22HeroSlider%22%7D" />

      <div data-component-id="src/app/khoa-hoc/page.tsx:171:6" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="171" data-component-end-line="255" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20max-w-7xl%20mx-auto%20px-4%20py-10%22%7D" className="container max-w-7xl mx-auto px-4 py-10">
        <div data-component-id="src/app/khoa-hoc/page.tsx:172:8" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="172" data-component-end-line="208" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20md%3Aflex-row%20items-start%20md%3Aitems-center%20justify-between%20mb-8%20gap-4%22%7D" className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div data-component-id="src/app/khoa-hoc/page.tsx:173:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="173" data-component-end-line="185" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20text-%5B18px%5D%20text-gray-600%22%7D" className="flex items-center gap-2 text-[18px] text-gray-600">
            <Link data-component-id="src/app/khoa-hoc/page.tsx:174:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="174" data-component-end-line="176" data-component-file="page.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22hover%3Atext-%5B%234A306D%5D%22%2C%22href%22%3A%22%2F%22%2C%22textContent%22%3A%22TRANG%20CH%E1%BB%A6%22%7D" className="hover:text-[#4A306D]" href="/">
              TRANG CHỦ
            </Link>
            <span data-component-id="src/app/khoa-hoc/page.tsx:177:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="177" data-component-end-line="177" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22%2F%22%7D">/</span>
            {selectedCategory ? (
              <span data-component-id="src/app/khoa-hoc/page.tsx:179:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="179" data-component-end-line="179" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-%5B900%5D%20text-%5B%23222%5D%22%7D" className="font-[900] text-[#222]">{categoryName}</span>
            ) : (
              <Link data-component-id="src/app/khoa-hoc/page.tsx:181:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="181" data-component-end-line="183" data-component-file="page.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22font-%5B900%5D%20text-%5B%23222%5D%22%2C%22href%22%3A%22%2Fkhoa-hoc%22%2C%22textContent%22%3A%22T%C3%A2%CC%81t%20ca%CC%89%20kho%CC%81a%20ho%CC%A3c%22%7D" className="font-[900] text-[#222]" href="/khoa-hoc">
                Tất cả khóa học
              </Link>
            )}
          </div>
          <div data-component-id="src/app/khoa-hoc/page.tsx:186:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="186" data-component-end-line="207" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20md%3Aw-%5B360px%5D%20grid%20grid-cols-2%20items-center%20gap-4%22%7D" className="w-full md:w-[360px] grid grid-cols-2 items-center gap-4">
            <form data-component-id="src/app/khoa-hoc/page.tsx:187:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="187" data-component-end-line="203" data-component-file="page.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22flex%20border%20border-gray-300%20bg-white%20w-full%20rounded-lg%20overflow-hidden%20shadow-sm%20transition-all%20hover%3Ashadow-md%20focus-within%3Ashadow-md%20focus-within%3Aborder-%5B%234A306D%5D%22%7D"
              onSubmit={handleSearch}
              className="flex border border-gray-300 bg-white w-full rounded-lg overflow-hidden shadow-sm transition-all hover:shadow-md focus-within:shadow-md focus-within:border-[#4A306D]"
            >
              <input data-component-id="src/app/khoa-hoc/page.tsx:191:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="191" data-component-end-line="196" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22className%22%3A%22flex-1%20px-4%20py-3%20outline-none%20bg-transparent%20text-%5B16px%5D%20placeholder-gray-400%22%2C%22value%22%3A%22%5Bvar%3AkeySearch%5D%22%7D"
                placeholder="Tìm khóa học..."
                className="flex-1 px-4 py-3 outline-none bg-transparent text-[16px] placeholder-gray-400"
                value={keySearch}
                onChange={handleSearchChange}
              />
              <button data-component-id="src/app/khoa-hoc/page.tsx:197:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="197" data-component-end-line="202" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22px-4%20bg-%5B%234A306D%5D%20text-white%20hover%3Abg-%5B%235d3d87%5D%20transition-colors%20flex%20items-center%20justify-center%22%7D"
                type="submit"
                className="px-4 bg-[#4A306D] text-white hover:bg-[#5d3d87] transition-colors flex items-center justify-center"
              >
                <Search data-component-id="src/app/khoa-hoc/page.tsx:201:16" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="201" data-component-end-line="201" data-component-file="page.tsx" data-component-name="Search" data-component-content="%7B%22elementName%22%3A%22Search%22%7D" size={20} />
              </button>
            </form>
            <div data-component-id="src/app/khoa-hoc/page.tsx:204:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="204" data-component-end-line="206" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20gap-2%20text-%5B18px%5D%20text-gray-600%22%7D" className="flex items-center justify-center gap-2 text-[18px] text-gray-600">
              <p data-component-id="src/app/khoa-hoc/page.tsx:205:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="205" data-component-end-line="205" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22C%C3%B3%20kh%C3%B3a%20h%E1%BB%8Dc%22%7D">Có {totalCourses} khóa học</p>
            </div>
          </div>
        </div>

        {/* Dòng chữ nhấp nháy ưu đãi */}
        {discountPercent > 0 && (
          <div data-component-id="src/app/khoa-hoc/page.tsx:212:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="212" data-component-end-line="218" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20text-center%22%7D" className="mb-6 text-center">
            <div data-component-id="src/app/khoa-hoc/page.tsx:213:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="213" data-component-end-line="217" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20px-6%20py-3%20rounded-full%20shadow%20inline-block%22%7D" className="bg-white px-6 py-3 rounded-full shadow inline-block">
              <span data-component-id="src/app/khoa-hoc/page.tsx:214:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="214" data-component-end-line="216" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22rainbow-blink%20font-bold%20text-sm%20md%3Atext-base%22%2C%22textContent%22%3A%22%F0%9F%94%A5%20%C4%90ANG%20%C3%81P%20D%E1%BB%A4NG%20%C6%AFU%20%C4%90%C3%83I%20GI%E1%BA%A2M%20%25%20CHO%20T%E1%BA%A4T%20C%E1%BA%A2%20C%C3%81C%20KH%C3%93A%20H%E1%BB%8CC%22%7D" className="rainbow-blink font-bold text-sm md:text-base">
                🔥 ĐANG ÁP DỤNG ƯU ĐÃI GIẢM {discountPercent}% CHO TẤT CẢ CÁC KHÓA HỌC
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div data-component-id="src/app/khoa-hoc/page.tsx:222:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="222" data-component-end-line="224" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%22%7D" className="text-center py-8">
            <p data-component-id="src/app/khoa-hoc/page.tsx:223:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="223" data-component-end-line="223" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22%C4%90ang%20t%E1%BA%A3i...%22%7D">Đang tải...</p>
          </div>
        ) : error ? (
          <div data-component-id="src/app/khoa-hoc/page.tsx:226:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="226" data-component-end-line="228" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%22%7D" className="text-center py-8">
            <p data-component-id="src/app/khoa-hoc/page.tsx:227:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="227" data-component-end-line="227" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-red-500%22%2C%22textContent%22%3A%22C%C3%B3%20l%E1%BB%97i%20x%E1%BA%A3y%20ra%20khi%20t%E1%BA%A3i%20d%E1%BB%AF%20li%E1%BB%87u%3A%22%7D" className="text-red-500">Có lỗi xảy ra khi tải dữ liệu: {error}</p>
          </div>
        ) : courses.length === 0 ? (
          <div data-component-id="src/app/khoa-hoc/page.tsx:230:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="230" data-component-end-line="236" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%22%7D" className="text-center py-8">
            <p data-component-id="src/app/khoa-hoc/page.tsx:231:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="231" data-component-end-line="235" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-500%22%7D" className="text-gray-500">
              {keySearch || selectedCategory
                ? `Không tìm thấy khóa học nào${keySearch ? ` với từ khóa "${keySearch}"` : ''}${selectedCategory ? ` trong danh mục "${categoryName}"` : ''}`
                : 'Không có khóa học nào'}
            </p>
          </div>
        ) : (
          <>
            <div data-component-id="src/app/khoa-hoc/page.tsx:239:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="239" data-component-end-line="243" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {courses.map((course) => (
                <CourseCard data-component-id="src/app/khoa-hoc/page.tsx:241:16" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="241" data-component-end-line="241" data-component-file="page.tsx" data-component-name="CourseCard" data-component-content="%7B%22elementName%22%3A%22CourseCard%22%7D" key={course.id} course={course} />
              ))}
            </div>
            {totalCourses > NUMBER_COURSES_PER_PAGE && (
              <div data-component-id="src/app/khoa-hoc/page.tsx:245:14" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="245" data-component-end-line="251" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%20overflow-x-auto%22%7D" className="mt-12 overflow-x-auto">
                <Pagination data-component-id="src/app/khoa-hoc/page.tsx:246:16" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="246" data-component-end-line="250" data-component-file="page.tsx" data-component-name="Pagination" data-component-content="%7B%22elementName%22%3A%22Pagination%22%7D"
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalCourses / NUMBER_COURSES_PER_PAGE)}
                  onPageChange={handlerPageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const KhoaHocPage: React.FC<KhoaHocPageProps> = ({}) => {
  return (
    <Suspense
      fallback={
        <div data-component-id="src/app/khoa-hoc/page.tsx:264:8" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="264" data-component-end-line="268" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20max-w-7xl%20mx-auto%20px-4%20pt-20%20pb-10%22%7D" className="container max-w-7xl mx-auto px-4 pt-20 pb-10">
          <div data-component-id="src/app/khoa-hoc/page.tsx:265:10" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="265" data-component-end-line="267" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-8%22%7D" className="text-center py-8">
            <p data-component-id="src/app/khoa-hoc/page.tsx:266:12" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="266" data-component-end-line="266" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22%C4%90ang%20t%E1%BA%A3i...%22%7D">Đang tải...</p>
          </div>
        </div>
      }
    >
      <KhoaHocPageContent data-component-id="src/app/khoa-hoc/page.tsx:271:6" data-component-path="src/app/khoa-hoc/page.tsx" data-component-line="271" data-component-end-line="271" data-component-file="page.tsx" data-component-name="KhoaHocPageContent" data-component-content="%7B%22elementName%22%3A%22KhoaHocPageContent%22%7D" />
    </Suspense>
  );
};

export default KhoaHocPage;
